import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { getNotifications, markAllNotificationsRead, markNotificationRead } from '../api';
import { AppNotification } from '../types';
import '../hangouts.css';

const REFRESH_MS = 60_000;

const ago = (iso: string) => {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.round(mins / 60)}h` : `${Math.round(mins / 1440)}d`;
};

// Header bell for signed-in customers: unread count plus the latest notifications, each deep-linking
// to its hangout. Refreshes every minute and on navigation.
export function NotificationBell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    getNotifications().then((n) => { setItems(n.items); setUnread(n.unreadCount); }).catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);
  useEffect(() => { setOpen(false); load(); }, [pathname, load]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const openItem = async (n: AppNotification) => {
    setOpen(false);
    if (!n.isRead) await markNotificationRead(n.id).catch(() => undefined);
    load();
    if (n.link) navigate(n.link);
  };

  return (
    <div className="hg-bell" ref={ref}>
      <button type="button" className="header-search" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <NotificationsNoneOutlinedIcon />
        {unread > 0 && <i>{unread > 9 ? '9+' : unread}</i>}
      </button>
      {open && (
        <div className="hg-bell-menu" role="menu">
          <div className="hg-bell-head">
            <b>Notifications</b>
            {unread > 0 && <button type="button" onClick={async () => { await markAllNotificationsRead().catch(() => undefined); load(); }}>Mark all read</button>}
          </div>
          {items.length === 0 && <p className="hg-bell-empty">You're all caught up.</p>}
          {items.map((n) => (
            <button key={n.id} type="button" role="menuitem" className={`hg-bell-item ${n.isRead ? '' : 'unread'}`} onClick={() => openItem(n)}>
              <span><b>{n.title}</b>{n.body && <small>{n.body}</small>}</span>
              <time dateTime={n.createdAt}>{ago(n.createdAt)}</time>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
